import handleError from '@fuse/core/errorHandler';
import { Button, TextField } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useMessage from 'src/app/hooks/useMessage';
import {
  useCreateClaimTypeMutation,
  useUpdateClaimTypeMutation,
} from 'src/app/services/claim/claimTypeApi';

const ALLOWED_PATTERN = /^[\p{L}\p{N}]/u;

const defaultValues = {
  name: '',
};

const ClaimTypeForm = ({ claimType, isEdit, onClose, refetch }) => {
  const { t } = useTranslation('translations');
  const { showMessage } = useMessage();
  const [update, { isLoading }] = useUpdateClaimTypeMutation();
  const [create, { isLoading: isCreateLoading, error }] = useCreateClaimTypeMutation();

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({
    mode: 'onChange',
    defaultValues: claimType ?? defaultValues,
  });

  const handleClose = () => {
    onClose?.();
    reset(defaultValues);
    refetch?.();
  };

  const makeRules = () => ({
    required: t('RequiredField') || true,
    validate: (value) => {
      const v = (value ?? '').trim();
      if (!v) return t('EmptyInput') || 'This field cannot be empty';

      if (!ALLOWED_PATTERN.test(v)) {
        return t('SpecialCharactersNotAllowed') || 'The first character must be a letter or number';
      }
      return true;
    },
  });

  const onChangeTrimStart = (fieldOnChange) => (e) => {
    const val = e.target.value.trimStart();
    fieldOnChange(val);
  };

  const onSubmit = async (values) => {
    if (!isValid) return;
    const payload = { name: (values.name ?? '').trim() };
    const message = isEdit ? t('ClaimTypeSuccessfullyUpdated') : t('ClaimTypeSuccessfullyAdded');
    const promise = isEdit ? update(payload) : create(payload);
    promise
      .unwrap()
      .then(() => {
        showMessage({ message, variant: 'success' });
        handleClose();
      })
      .catch((err) => handleError({ response: err }));
  };

  return (
    <>
      {error && <p className="text-red-500">{t('RequiredField')}</p>}
      <form onSubmit={handleSubmit(onSubmit)} className="pt-10 max-w-7xl">
        <div className="mb-10">
          <Controller
            name="name"
            control={control}
            rules={makeRules()}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label={t('Title')}
                error={!!errors.name}
                helperText={errors.name?.message}
                variant="outlined"
                required
                onChange={onChangeTrimStart(field.onChange)}
              />
            )}
          />
        </div>
        <Button
          disabled={isLoading || isCreateLoading || !isValid}
          type="submit"
          className="w-full"
          variant="contained"
          color="secondary"
        >
          <span className="mx-8">{t('Save')}</span>
        </Button>
      </form>
    </>
  );
};

export default ClaimTypeForm;
